import { Component, EventEmitter, Output } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/core/services/auth.service';
import { calcularIniciais } from 'src/app/core/utils/string.utils';

type Perfil = 'GESTOR' | 'COLABORADOR';

export type NavItem = {
  label: string;
  icon: string;
  route?: string | null;
  externalUrl?: string;
  queryParams?: Record<string, any>;
  aria?: string;
  adminOnly?: boolean;
  perfilRequerido?: Perfil;
  children?: NavItem[];
  expanded?: boolean;
  disabled?: boolean;
  hint?: string;
  tourId?: string;
};

export type NavGrupo = {
  label: string;
  items: NavItem[];
};

@Component({
  selector: 'app-shared-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss']
})
export class MenuComponent {
  @Output() logout = new EventEmitter<void>();
  @Output() navClicked = new EventEmitter<void>();

  isPinned = false;
  isHovering = false;
  isGestorUser = false;
  userPerfil: Perfil = 'COLABORADOR';

  // Grupos de navegação — sidebar em 2 blocos (Operação / Configuração)
  // Itens sem perfilRequerido são visíveis para todos os perfis.
  // Itens com perfilRequerido='GESTOR' aparecem apenas para gestores.
  // Itens disabled são exibidos desabilitados (em breve).
  readonly grupos: NavGrupo[] = [
    {
      label: 'Operação',
      items: [
        { label: 'Home',       icon: 'pi-th-large',  route: '/home',      aria: 'Home',       tourId: 'menu-home',       perfilRequerido: 'GESTOR' },
        { label: 'Pessoas',    icon: 'pi-users',      route: '/pessoas',   aria: 'Pessoas',    tourId: 'menu-times',      perfilRequerido: 'GESTOR' },
        { label: 'Relatórios', icon: 'pi-chart-bar',  route: '/relatorios',aria: 'Relatórios', tourId: 'menu-relatorios', perfilRequerido: 'GESTOR' },
        // Itens do colaborador (sem acesso à área gestor)
        { label: 'Meu Painel',      icon: 'pi-id-card',   route: '/meu-painel',      aria: 'Meu Painel',      perfilRequerido: 'COLABORADOR' },
        { label: 'Meus Relatórios', icon: 'pi-chart-bar',  route: '/meus-relatorios', aria: 'Meus Relatórios', perfilRequerido: 'COLABORADOR' },
      ]
    },
    {
      label: 'Configuração',
      items: [
        { label: 'Empresa', icon: 'pi-building', route: '/empresa', aria: 'Empresa', tourId: 'menu-organizacao', perfilRequerido: 'GESTOR' },
      ]
    }
  ];

  // Cache computado uma vez no constructor — evita nova referência a cada change detection
  // (que causaria ExpressionChangedAfterItHasBeenCheckedError e loop de render em prod)
  filteredGrupos: NavGrupo[] = [];
  filteredItems: NavItem[] = [];

  constructor(private authService: AuthService, private router: Router) {
    this.isGestorUser = this.authService.isGestor();
    this.userPerfil = this.resolveUserPerfil();
    this.filteredGrupos = this.computeFilteredGrupos();
    this.filteredItems = this.filteredGrupos.flatMap(g => g.items);
  }

  private computeFilteredGrupos(): NavGrupo[] {
    return this.grupos
      .map(grupo => ({
        ...grupo,
        items: grupo.items.filter(item => this.isItemVisible(item))
      }))
      .filter(grupo => grupo.items.length > 0);
  }

  private resolveUserPerfil(): Perfil {
    const perfil = (this.authService.getUser()?.perfil ?? '').toUpperCase();
    if (perfil === 'GESTOR') return 'GESTOR';
    return 'COLABORADOR';
  }

  isItemVisible(item: NavItem): boolean {
    // Itens sem restrição de perfil aparecem para todos (ex: itens disabled "em breve")
    if (!item.perfilRequerido) return true;

    if (this.userPerfil === 'GESTOR') {
      // GESTOR vê apenas itens de GESTOR (não vê itens exclusivos de COLABORADOR)
      return item.perfilRequerido === 'GESTOR';
    }

    if (this.userPerfil === 'COLABORADOR') {
      // COLABORADOR vê apenas itens de COLABORADOR
      return item.perfilRequerido === 'COLABORADOR';
    }

    return false;
  }

  // Retorna true se o grupo passado é o último grupo visível — usado para omitir o separador.
  // Usa cache computado no constructor (não recomputa a cada change detection).
  isUltimoGrupo(grupo: NavGrupo): boolean {
    return this.filteredGrupos[this.filteredGrupos.length - 1]?.label === grupo.label;
  }

  // Abreviação de 4 letras exibida no estado fechado do sidebar — mantém altura do wrapper
  // e evita shift vertical dos icones ao abrir/fechar. Remove acentos antes de cortar
  // (ex: "Operação" → "OPER", "Configuração" → "CONF").
  getGrupoAbrev(label: string): string {
    const normalized = label
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toUpperCase();
    return normalized.slice(0, 4);
  }

  abrirConfiguracoes(): void {
    this.router.navigate(['/perfil']);
  }

  get avatarUrl(): string | null {
    return this.authService.getUser()?.avatarUrl ?? null;
  }

  get isExpanded(): boolean {
    return this.isPinned || this.isHovering;
  }

  onMouseEnter(): void {
    this.isHovering = true;
  }

  onMouseLeave(): void {
    this.isHovering = false;
  }

  togglePinned(): void {
    this.isPinned = !this.isPinned;
  }

  toggleItem(item: NavItem): void {
    if (!item.children?.length) {
      return;
    }

    item.expanded = !item.expanded;

    if (item.expanded && !this.isExpanded) {
      this.isPinned = true;
    }
  }

  get userName(): string {
    const user = this.authService.getUser();
    if (!user) return 'Usuario';

    const nome = user.nome?.trim() || '';
    const sobrenome = user.sobrenome?.trim() || '';
    const nomeCompleto = `${nome} ${sobrenome}`.trim();

    return nomeCompleto || 'Usuario';
  }

  get userRole(): string {
    const role = this.authService.getUser()?.perfil?.trim();
    return role ? this.formatRole(role) : 'Colaborador';
  }

  get userInitials(): string {
    const user = this.authService.getUser();
    return calcularIniciais(user?.nome, user?.sobrenome);
  }

  private formatRole(role: string): string {
    const normalized = role.toLowerCase();
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }
}
